import { KIASI_PRICE_SCALE } from './contracts';
import type { KiasiInterval } from './contracts';

/** One histogram level in display units (dollars, shares). Shared by history profiles and live bars. */
export interface VolumePriceLevel {
    readonly price: number;
    readonly bid: number;
    readonly ask: number;
    readonly mid: number;
    readonly premarket: number;
    readonly regular: number;
    readonly postmarket: number;
    readonly total: number;
}

/** An owned copy of one Kiasi bar in display units; never a borrowed wire view. */
export interface VolumeBarSnapshot {
    readonly symbol: string;
    readonly interval: KiasiInterval;
    readonly openTimeMs: number;
    /** True once the server published the bar's final version. */
    readonly closed: boolean;
    readonly sequence: number;
    readonly priceCarried: boolean;
    /** `null` before the day's first priced bar; otherwise all four prices are present. */
    readonly open: number | null;
    readonly high: number | null;
    readonly low: number | null;
    readonly close: number | null;
    readonly tradeCount: number;
    readonly totalVolume: number;
    readonly vwapVolume: number;
    /** Sum of price times size over VWAP-eligible prints, in dollar-shares. */
    readonly vwapNotional: number;
    readonly vwap: number | null;
    readonly poc: number;
    readonly vah: number;
    readonly val: number;
    readonly delta: number;
    readonly bidVolume: number;
    readonly askVolume: number;
    readonly midVolume: number;
    readonly premarketVolume: number;
    readonly regularVolume: number;
    readonly postmarketVolume: number;
    readonly openAuctionVolume: number;
    readonly closeAuctionVolume: number;
    /** Ascending price order. */
    readonly levels: readonly VolumePriceLevel[];
}

export function scaledPriceToDollars(scaledPrice: number): number {
    return scaledPrice / KIASI_PRICE_SCALE;
}

export interface KiasiLiveUrls {
    /** Primary endpoint: HTTPS WebTransport, or a ws/wss URL for a WebSocket-only connection. */
    readonly url: string;
    readonly websocketUrl?: string;
}

export interface KiasiLiveUrlOptions {
    /**
     * Try HTTP/3 WebTransport first on an absolute HTTPS base, falling back to
     * WebSocket. Off by default: the client only falls back after WebTransport
     * fails, which costs a timeout on every connect while the ingress does not
     * serve HTTP/3 (PLAT-12347).
     */
    readonly preferWebTransport?: boolean;
}

/**
 * Derives the live endpoints from the HTTP base. A relative base is the
 * dev-server proxy, and an HTTP base is a local binary; neither speaks HTTP/3,
 * so both go straight to WebSocket, as does an HTTPS base unless
 * `preferWebTransport` is set. `origin` resolves only a relative base; an
 * absolute base ignores it, so a packaged Electron renderer's `"null"` origin
 * (windows load from `file://`) cannot break an absolute hostname.
 */
export function resolveKiasiLiveUrls(
    kiasiApi: string,
    origin: string,
    { preferWebTransport = false }: KiasiLiveUrlOptions = {},
): KiasiLiveUrls {
    const proxied = !/^[a-z]+:\/\//i.test(kiasiApi);
    const base = proxied ? new URL(kiasiApi, origin) : new URL(kiasiApi);
    const path = base.pathname.replace(/\/+$/, '');
    const hostAndPath = `${base.host}${path}`;
    if (preferWebTransport && base.protocol === 'https:' && !proxied) {
        return {
            url: `https://${hostAndPath}/live/webtransport`,
            websocketUrl: `wss://${hostAndPath}/live/ws`,
        };
    }
    const wsProtocol = base.protocol === 'https:' ? 'wss' : 'ws';
    return { url: `${wsProtocol}://${hostAndPath}/live/ws` };
}
